import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  EyeOff,
  Lock,
  FileCheck2,
  History,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

export default function PriorHsecurity() {
  const [activeTab, setActiveTab] = useState(0);
  const securitySpecs = [
    {
      id: "SEC-01",
      icon: EyeOff,
      title: "Zero-Persistence ePHI Runtime",
      category: "Data Privacy",
      summary:
        "Volatile in-memory processing guarantees clinical records are never stored at rest during pipeline execution.",
      verification: "Stateless Worker Architecture",
      protocol: "RAM-Only Execution // Ephemeral GC",
      details: [
        "Zeebe task workers pull FHIR resources directly to heap memory with immediate garbage collection post-adjudication.",
        "Zero intermediate database caching or log retention of patient identifiable health data.",
        "Automatic payload redaction across telemetry and distributed tracing spans.",
      ],
      auditMetric: "0.00ms Storage Footprint",
    },
    {
      id: "SEC-02",
      icon: Lock,
      title: "mTLS 1.3 & Scoped Delegation",
      category: "Network & Transport",
      summary:
        "Zero-trust communication mesh enforcing cryptographic mutual identity verification between EHRs and payer engines.",
      verification: "FIPS 140-3 Cryptography",
      protocol: "TLS_AES_256_GCM_SHA384",
      details: [
        "Bidirectional certificate handshakes required on all CDS Hooks and PAS gateway ingress points.",
        "Granular OAuth 2.0 SMART-on-FHIR scopes restrict worker access strictly to pre-authorized CPT/ICD resources.",
        "Automated certificate rotation and cipher deprecation monitoring across all edge clusters.",
      ],
      auditMetric: "256-bit AES Encryption",
    },
    {
      id: "SEC-03",
      icon: FileCheck2,
      title: "HIPAA BAA & SOC 2 Type II",
      category: "Attestation & Audit",
      summary:
        "Full institutional compliance coverage backed by third-party attestations and signed enterprise legal agreements.",
      verification: "AICPA SOC 2 Certified",
      protocol: "Annual Third-Party Attestation",
      details: [
        "Comprehensive Business Associate Agreement (BAA) execution covering all deployment topologies.",
        "Continuous compliance monitoring against AICPA Trust Services Criteria (Security, Availability, Confidentiality).",
        "Mandatory vulnerability disclosure programs and regular penetration assessments.",
      ],
      auditMetric: "100% Policy Attestation",
    },
    {
      id: "SEC-04",
      icon: History,
      title: "Cryptographic Audit Ledger",
      category: "Forensics & Traceability",
      summary:
        "Tamper-evident operational logging providing sub-second provenance without exposing patient identifiers.",
      verification: "CMS-0057-F Audit Ready",
      protocol: "SHA-256 Chained Hash Verification",
      details: [
        "Every decision rule invocation produces a verifiable signature proving exactly which clinical policy authorized the claim.",
        "Role-based access logs with immutable timestamps for CMS regulatory inspection readiness.",
        "Complete segregation between operational performance metrics and clinical payload streams.",
      ],
      auditMetric: "Tamper-Evident Signatures",
    },
  ];
  const current = securitySpecs[activeTab];
  return (
    <section className="relative w-full lg:py-24 md:py-16 py-10 px-6 sm:px-8 lg:px-12  overflow-hidden">
      <div
        className="pointer-events-none absolute top-0 left-0 w-full h-full opacity-20 "
        style={{
          background:
            "linear-gradient(135deg, #62d4f7 0%, #5dc9f0 40%, #3579be 60%",
        }}
      />
      <div className="3xl:w-4/5 xl:w-[90%] mx-auto space-y-16">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-slate-200">
          <div>
            <div className="inline-flex items-center gap-2 px-0.5 py-1.5 mb-4">
              <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-700">
                GOVERNANCE LEDGER
              </span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl max-w-4xl font-bold tracking-tight text-slate-950">
              Healthcare Compliance &amp;{" "}
              <span className="italic font-serif font-normal text-[#5dc9f0]">
                Security Guardrails
              </span>
            </h2>
          </div>
        </div>
        <div className="space-y-4 lg:hidden">
          {securitySpecs.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.id}
                className="bg-white rounded-2xl border border-slate-300/80 p-6 shadow-sm space-y-4"
              >
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-[#000036] text-white flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-[#000036] tracking-tight mt-0.5">
                        {item.title}
                      </h3>
                    </div>
                  </div>
                </div>
                <p className="text-base text-slate-600 leading-relaxed">
                  {item.summary}
                </p>
                <div className="space-y-2 pt-1">
                  <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-slate-400 block">
                    Architecture Details
                  </span>
                  {item.details.map((detail, dIdx) => (
                    <div
                      key={dIdx}
                      className="flex items-start gap-2.5 text-sm text-slate-600 leading-relaxed "
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#5dc9f0] shrink-0 mt-0.5" />
                      <span>{detail}</span>
                    </div>
                  ))}
                </div>

                <div className="pt-3 border-t border-slate-100 flex flex-wrap gap-3 items-center justify-between text-sm font-mono text-slate-500">
                  <span>{item.protocol}</span>
                  <span className="font-bold text-[#000036]">
                    {item.auditMetric}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="rounded-xl border border-slate-300/80 bg-white shadow-xl shadow-slate-200/50 overflow-hidden hidden lg:grid grid-cols-1 lg:grid-cols-12">
          <div className="lg:col-span-5 border-b lg:border-b-0 lg:border-r border-slate-200 divide-y divide-slate-100 bg-slate-50/50">
            {securitySpecs.map((item, idx) => {
              const Icon = item.icon;
              const isSelected = activeTab === idx;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(idx)}
                  className={`w-full text-left p-6 transition-all duration-200 flex items-start justify-between gap-4 group `}
                  style={{
                    borderLeft: isSelected
                      ? "4px solid #000036"
                      : "4px solid transparent",
                    background: isSelected ? "#e0f2fe" : "transparent",
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`h-10 w-10 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                        isSelected
                          ? "bg-[#000036] text-white"
                          : "bg-slate-200/80 text-slate-600 group-hover:bg-slate-300"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono font-bold uppercase tracking-wider text-sky-700 bg-sky-50 px-2 py-0.5 rounded">
                          {item.category}
                        </span>
                      </div>
                      <h3
                        className={`text-base font-bold transition-colors ${
                          isSelected ? "text-[#000036]" : "text-slate-800"
                        }`}
                      >
                        {item.title}
                      </h3>
                      <p className=" line-clamp-2 text-sm sm:text-base text-slate-600 font-normal leading-relaxed">
                        {item.summary}
                      </p>
                    </div>
                  </div>
                  <ArrowRight
                    className={`w-4 h-4 shrink-0 transition-transform ${
                      isSelected
                        ? "text-[#000036] translate-x-1"
                        : "text-slate-300 group-hover:text-slate-500"
                    }`}
                  />
                </button>
              );
            })}
          </div>
          <div className="lg:col-span-7 p-6 sm:p-8 flex flex-col justify-between bg-white relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
                className="space-y-8"
              >
                <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-100 pb-5">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-sm font-bold text-[#000036] capitalize tracking-wider">
                      {current.verification}
                    </span>
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-[#000036] tracking-tight">
                    {current.title}
                  </h3>
                  <p className="text-base text-slate-600 leading-relaxed">
                    {current.summary}
                  </p>
                </div>
                <div className="space-y-3 pt-2">
                  <span className="text-xs font-mono font-bold uppercase tracking-wider text-slate-400 block">
                    Enforcement Architecture
                  </span>
                  <div className="space-y-2.5">
                    {current.details.map((detail, dIdx) => (
                      <div
                        key={dIdx}
                        className="flex items-start gap-3 text-sm sm:text-base text-slate-600 font-normal leading-relaxed p-3 rounded-xl bg-slate-50 border border-slate-100"
                      >
                        <CheckCircle2 className="w-4 h-4 text-[#5dc9f0] shrink-0 mt-0.5" />
                        <span>{detail}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pt-5 border-t border-slate-100 flex items-center justify-between text-sm font-mono text-slate-500">
                    <span>{current.protocol}</span>
                    <span className="font-bold text-[#000036]">
                      {current.auditMetric}
                    </span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
